import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import type { ReactNode } from "react";
import { ConfirmProvider, DEFAULT_SPRITE_BASE, IconProvider, ToastProvider } from "@xinghai/ui";
import { ApiError } from "../lib/api";

/** One client for the app; tests build their own with retries off. */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          // Never retry auth / permission / validation failures.
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          return failureCount < 2;
        },
        staleTime: 15_000,
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  });
}

export interface ProvidersProps {
  children: ReactNode;
  client?: QueryClient;
  /** Override the icon sprite (tests, sub-path deployments). */
  spriteBase?: string;
}

/** Query cache, toasts, confirm dialogs and the icon sprite, in that order. */
export function Providers({ children, client, spriteBase }: ProvidersProps) {
  const [fallback] = useState(createQueryClient);
  const qc = client ?? fallback;
  return (
    <QueryClientProvider client={qc}>
      <IconProvider spriteBase={spriteBase ?? DEFAULT_SPRITE_BASE}>
        <ToastProvider>
          <ConfirmProvider>{children}</ConfirmProvider>
        </ToastProvider>
      </IconProvider>
    </QueryClientProvider>
  );
}
